import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';
import { useWindowDimensions } from '../hooks/useWindowDimensions';
import { updateAreaOpen, updateTransition } from '../store/globalReducer';
import { selectPin, deselectPins, updateSelectedTemple } from '../store/templesReducer';
import IconArrow from './icons/iconArrow';

import '../assets/scss/components/map.scss';
import '../assets/scss/components/info.scss';

import AreaPinImg from '../assets/img/common/pin_area.png'
import iconPin from '../assets/img/common/icon_pin.svg';

import cloudA from '../assets/img/common/cloudA.png';
import cloudB from '../assets/img/common/cloudB.png';
import cloudC from '../assets/img/common/cloudC.png';

import whaleImg from '../assets/img/index/map-index-whale.png';
import shipImg from '../assets/img/area/map-area-ship.png';

export default function Map({ isIndex, mapImage, pinList, mapTransform, updateMapTransform }) {
  const dispatch = useDispatch();
  const location = useLocation();

  const containerEl = useRef(null);
  const mapEl = useRef(null);
  const imageEl = useRef(null);

  const { winWidth, winHeight } = useWindowDimensions();
  const maxWidth = 1410;
  const maxHeight = 850;

  const selectedTemple = useSelector(state => state.temples.selectedTemple);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [bounds, setBounds] = useState({ top: 0, bottom: 0, left: 0, right: 0 });
  const [isTwoTouches, setIsTwoTouches] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [canDrag, setCanDrag] = useState(imageLoaded && !isTwoTouches);
  const [isResize, setIsResize] = useState(false);

  useEffect(() => {
    let dragVal = false;

    if (imageLoaded && !isTwoTouches) {
      if (winWidth >= maxWidth && winHeight >= maxHeight) {
        dragVal = false;
      }
      else if (winHeight >= maxHeight - 150) {
        dragVal = 'x';
      }
      else if (winWidth >= maxWidth) {
        dragVal = 'y';
      }
      else {
        dragVal = true;
      }
    }

    setCanDrag(dragVal);
  }, [canDrag, imageLoaded, isTwoTouches, winWidth, winHeight, maxWidth, maxHeight]);

  useEffect(() => {
    const containerRect = containerEl.current.getBoundingClientRect();
    const newBounds = {
      top: -(imageEl.current.naturalHeight - containerRect.height),
      bottom: 0,
      left: -(imageEl.current.naturalWidth - containerRect.width),
      right: 0
    };

    setBounds(newBounds);
  }, [canDrag, imageLoaded, containerEl, imageEl, winWidth, winHeight]);

  useEffect(() => {
    if (!isResize) {
      setIsResize(true);
      setTimeout(() => setIsResize(false), 3000);
    }

    const mapRect = mapEl.current.getBoundingClientRect();
    const coordsVal = checkBounds({ x: mapRect.x, y: mapRect.y });
    setCoords(coordsVal);
    setIsAnimating(true);
    // setTimeout(() => setIsAnimating(false), 600);
  }, [winWidth, winHeight]);

  useEffect(() => {
    dispatch(deselectPins());
    dispatch(updateSelectedTemple(false));
  }, [location]);

  const checkBounds = coords => {
    let tempCoords = coords;

    if (tempCoords.x > 0) tempCoords.x = 0;
    if (tempCoords.x < -Math.abs(imageEl.current.offsetWidth - Math.min(winWidth, maxWidth))) {
      tempCoords.x = -Math.abs(imageEl.current.offsetWidth - Math.min(winWidth, maxWidth));
    }
    if (tempCoords.y > 0) tempCoords.y = 0;
    if (tempCoords.y < -Math.abs(imageEl.current.offsetHeight - Math.min(winHeight, maxHeight))) {
      tempCoords.y = -Math.abs(imageEl.current.offsetHeight - Math.min(winHeight, maxHeight));
    }

    return tempCoords;
  }

  // Event handlers
  const handleDragEnd = (e) => {
    if (!e.target || typeof e.target.getBoundingClientRect !== 'function') return;
    const mapRect = e.target.getBoundingClientRect();
    dispatch(updateMapTransform({ x: mapRect.x, y: mapRect.y }));
  }

  const handlePinClick = (e, index) => {
    e.preventDefault();
    dispatch(deselectPins());
    dispatch(updateSelectedTemple(false));

    // Center map on the clicked pin
    const pinRect = e.currentTarget.getBoundingClientRect();
    const imageRect = imageEl.current.getBoundingClientRect();
    const distX = (winWidth / 2) - pinRect.x - (pinRect.width / 2);
    const distY = (winHeight / 2) - pinRect.y - (pinRect.height / 2);
    const coordsVal = checkBounds({
      x: imageRect.x + distX,
      y: imageRect.y + distY
    });

    setCoords(coordsVal);
    dispatch(updateMapTransform(coordsVal));
    setIsAnimating(true);

    // Select pin and show modal
    setTimeout(() => {
      dispatch(selectPin(index));
      dispatch(updateSelectedTemple(index));
    }, selectedTemple ? 500 : 0);

    setTimeout(() => setIsAnimating(false), 600);
  }

  const handleCloseClick = e => {
    e.preventDefault();
    dispatch(deselectPins());
    dispatch(updateSelectedTemple(false));
  }

  const handleAreaOpenClick = e => {
    e.preventDefault();
    dispatch(deselectPins());
    dispatch(updateSelectedTemple(false));
    dispatch(updateAreaOpen(true));
    dispatch(updateTransition(true));
  }

  const handleTouchStart = e => {
    if (e.touches) {
      setIsTwoTouches(e.touches.length >= 2);
    }
  }

  const handleTouchEnd = e => {
    setIsTwoTouches(false);
  }

  return (
    <div className="map">
      <motion.div className="map__inner" ref={containerEl}>
        <motion.div
          className="map__image"
          ref={mapEl}
          initial={{ x: Math.min(mapTransform.x, 0), y: Math.min(mapTransform.y, 0) }}
          drag={canDrag}
          dragPropagation
          dragConstraints={bounds}
          dragElastic={0.1}
          dragMomentum={!isResize}
          whileDrag={{ cursor: 'grabbing' }}
          onTouchStart={e => handleTouchStart(e)}
          onTouchEnd={e => handleTouchEnd(e)}
          onDragEnd={(event, info) => handleDragEnd(event)}
          animate={ isAnimating ? { x: coords.x, y: coords.y, transition: { ease: 'easeOut', duration: 0.5 } } : false}
          onAnimationComplete={() => setIsAnimating(false)}>
            <img ref={imageEl} style={{ width: `${maxWidth}px`, height: `${maxHeight}px` }} src={mapImage} onLoad={() => setImageLoaded(true)} alt="須崎 神社仏閣マップ" />

          {isIndex && (
            <a href="#"
              className="map__link"
              style={{ top: `${427}px`, left: `${340}px` }}
              onClick={e => handleAreaOpenClick(e)}>
              <motion.div
                initial={{ y: '120%' }}
                animate={{ y: '0%', transition: { ease: 'backOut', duration: 0.5, delay: 0.3 } }}>
                <img src={AreaPinImg} alt="海のまちエリア" />
              </motion.div>
            </a>
          )}

          {pinList.map((pin, index) => (
            pin.icon ? (
              <a href="#"
                className={`map__link ${pin.isSelected ? "is-selected" : ""}`}
                onClick={e => handlePinClick(e, pin.id)}
                key={pin.id}
                style={{ top: `${pin.coord_y}px`, left: `${pin.coord_x}px` }}>
                <motion.div
                  initial={{ y: '120%' }}
                  animate={{ y: '0%', transition: { ease: 'backOut', duration: 0.5, delay: 0.3 + index * 0.1 } }}>
                  <img src={`${process.env.PUBLIC_URL}/pin_images/pin_${pin.icon}.png`} draggable="false" alt={pin.name_jp} />
                  <img src={`${process.env.PUBLIC_URL}/pin_images/pin_${pin.icon}_select.png`} draggable="false" alt={pin.name_jp} />
                </motion.div>
              </a>
            ) : (
              <motion.div
                className="map__spot"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 0.3, delay: 0.3 + index * 0.1, ease: 'linear' } }}
                key={pin.id}
                style={{ top: `${pin.coord_y}px`, left: `${pin.coord_x}px` }}>
              </motion.div>
            )
          ))}

          {isIndex && (
            <>
              <div className="map__clouds map__clouds--index">
                <motion.div
                  className="map__cloudbelt map__cloudbelt--top"
                  animate={{ x: '50%' }}
                  transition={{ repeat: Infinity, duration: 40, ease: 'linear' }}>
                  <div className="map__cloudwrap">
                    <div className="map__cloud map__cloud--first"><img src={cloudB} alt="" /></div>
                    <div className="map__cloud map__cloud--second"><img src={cloudA} alt="" /></div>
                  </div>
                  <div className="map__cloudwrap">
                    <div className="map__cloud map__cloud--first"><img src={cloudB} alt="" /></div>
                    <div className="map__cloud map__cloud--second"><img src={cloudA} alt="" /></div>
                  </div>
                </motion.div>
                <motion.div
                  className="map__cloudbelt map__cloudbelt--mid"
                  animate={{ x: '50%' }}
                  transition={{ repeat: Infinity, duration: 30, ease: 'linear' }}>
                  <div className="map__cloudwrap"><div className="map__cloud map__cloud--third"><img src={cloudC} alt="" /></div></div>
                  <div className="map__cloudwrap"><div className="map__cloud map__cloud--third"><img src={cloudC} alt="" /></div></div>
                </motion.div>
                <motion.div
                  className="map__cloudbelt map__cloudbelt--bottom"
                  animate={{ x: '50%' }}
                  transition={{ repeat: Infinity, duration: 35, ease: 'linear' }}>
                  <div className="map__cloudwrap">
                    <div className="map__cloud map__cloud--fourth"><img src={cloudB} alt="" /></div>
                    <div className="map__cloud map__cloud--fifth"><img src={cloudC} alt="" /></div>
                    <div className="map__cloud map__cloud--sixth"><img src={cloudB} alt="" /></div>
                  </div>
                  <div className="map__cloudwrap">
                    <div className="map__cloud map__cloud--fourth"><img src={cloudB} alt="" /></div>
                    <div className="map__cloud map__cloud--fifth"><img src={cloudC} alt="" /></div>
                    <div className="map__cloud map__cloud--sixth"><img src={cloudB} alt="" /></div>
                  </div>
                </motion.div>
              </div>
              <div className="map__whale"><img src={whaleImg} alt="鯨" /></div>
            </>
          )}

          {!isIndex && (
            <>
              <div className="map__clouds map__clouds--area">
                <motion.div
                  className="map__cloudbelt map__cloudbelt--top"
                  animate={{ x: '50%' }}
                  transition={{ repeat: Infinity, duration: 40, ease: 'linear' }}>
                  <div className="map__cloudwrap">
                    <div className="map__cloud map__cloud--first"><img src={cloudB} alt="" /></div>
                    <div className="map__cloud map__cloud--second"><img src={cloudC} alt="" /></div>
                  </div>
                  <div className="map__cloudwrap">
                    <div className="map__cloud map__cloud--first"><img src={cloudB} alt="" /></div>
                    <div className="map__cloud map__cloud--second"><img src={cloudC} alt="" /></div>
                  </div>
                </motion.div>
                <motion.div
                  className="map__cloudbelt map__cloudbelt--bottom"
                  animate={{ x: '50%' }}
                  transition={{ repeat: Infinity, duration: 35, ease: 'linear' }}>
                  <div className="map__cloudwrap">
                    <div className="map__cloud map__cloud--third"><img src={cloudA} alt="" /></div>
                    <div className="map__cloud map__cloud--fourth"><img src={cloudB} alt="" /></div>
                  </div>
                  <div className="map__cloudwrap">
                    <div className="map__cloud map__cloud--third"><img src={cloudA} alt="" /></div>
                    <div className="map__cloud map__cloud--fourth"><img src={cloudB} alt="" /></div>
                  </div>
                </motion.div>
              </div>
              <div className="map__ship"><img src={shipImg} alt="船" /></div>
            </>
          )}
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {selectedTemple && (
          <motion.div
            initial={{ y: '110%' }}
            animate={{ y: '0%', transition: { ease: 'easeOut' } }}
            exit={{ y: '110%', transition: { ease: 'easeIn' } }}
            className="info">
              <div className="info__inner">
                <div className="info__head"><h3 className="info__title">{selectedTemple.map_title}</h3></div>
                <div className="info__body">
                  <figure className="info__image" style={{
                    backgroundImage: selectedTemple.thumb_url
                    ? `url(${process.env.PUBLIC_URL}/detail_images/${selectedTemple.thumb_url})`
                    : `url(${require('../assets/img/common/no_image.jpg')})`
                  }}>
                  </figure>
                  <div className="info__textbox">
                    <div className="info__location">
                      <i><img src={iconPin} /></i>
                      <p>{selectedTemple.name_jp}</p>
                    </div>
                    <div className="info__btn"><Link to={`/${selectedTemple.name_en}`}><span>詳しくはこちら</span></Link></div>
                  </div>
                </div>
                <div className="info__close">
                  <a href="#" onClick={handleCloseClick}><IconArrow></IconArrow></a>
                </div>
              </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}